<template>
  <div class="phone-filter-bar">
    <div class="phone-filter-bar__row">
      <slot name="search" />
      <FilterChip
        class="phone-filter-bar__open"
        is-text
        icon="sliders"
        :label="openLabel"
        :active="activeCount > 0 || dialogOpen"
        @toggle="dialogOpen = true"
      />
    </div>
    <div v-if="activeCount" class="phone-filter-bar__in-use">
      <FilterChipsContext in-use>
        <slot name="filters" />
      </FilterChipsContext>
      <button type="button" class="phone-filter-bar__clear" @click="$emit('clear-all')">
        {{ $t('filters.clear-all') }}
      </button>
    </div>

    <FiltersDialog
      v-if="dialogOpen"
      :title="$t('filters.title')"
      :has-active-filters="activeCount > 0"
      @close="dialogOpen = false"
      @clear-all="$emit('clear-all')"
    >
      <FilterChipsContext list>
        <slot name="filters" />
      </FilterChipsContext>
    </FiltersDialog>
  </div>
</template>

<script>
import FilterChip from '@/components/filters/FilterChip';
import FilterChipsContext from '@/components/filters/FilterChipsContext';
import FiltersDialog from '@/components/filters/FiltersDialog';

// A list's search and filters on a phone, as in the tenders inbox: the search and a
// "Filtres (N)" button on one line, the filters in use on a line of their own that scrolls
// sideways (a chip edits its filter, its × removes it), and every filter in a panel, one line
// each. The page gives its search and its filter chips (`#filters`, drawn once per place: pass it
// with v-slot) and says how many filters are set.
export default {
  name: 'PhoneFilterBar',
  components: { FilterChip, FilterChipsContext, FiltersDialog },
  props: {
    activeCount: { type: Number, default: 0 },
  },
  data() {
    return { dialogOpen: false };
  },
  computed: {
    openLabel() {
      const label = this.$t('filters.title');
      return this.activeCount ? `${label} (${this.activeCount})` : label;
    },
  },
};
</script>

<style lang="scss">
.phone-filter-bar {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;

  &__row {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;

    .base-search-bar-container,
    .v-input {
      flex: 1;
      min-width: 0;
      margin: 0;
    }

    // As tall as the button beside it, on every page.
    .base-search-bar-container__input {
      height: 40px;
    }

    // A solo Vuetify field keeps 48px for itself, under a shorter box: centred on the button, the
    // box would sit high.
    .v-text-field.v-text-field--solo .v-input__control {
      min-height: 0;
    }
  }

  &__open .filter-chip__btn {
    min-height: 40px;
    white-space: nowrap;
  }

  // Runs to the screen's edges, so a chip scrolled halfway out reads as "more this way".
  &__in-use {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    margin: 0 (-$phone-page-gutter);
    padding: 2px $phone-page-gutter;
    overflow-x: auto;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  &__clear {
    flex: none;
    min-height: 36px;
    padding: 0 4px;
    font-size: 13px;
    font-weight: bold;
    color: $color-slate-500;
    white-space: nowrap;
  }
}
</style>
